import {PageHeader, SaveCancelFooter} from '@/components/molecules'
import {TargetSetUp} from '@/containers'
import {useAppDispatch, useAppSelector} from '@/store'
import {useState, useEffect} from 'react'
import {resetSalesTarget, setSalesTargetFormData} from '@/store/slices/salesTargetSlice'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ProgressIndicator, Breadcrumb} from '@/components/atoms'
import {useNavigate} from 'react-router-dom'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useSalesTargets} from '@/hooks/useSalesTargets'
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants'
import {useLocation} from 'react-router-dom'
import {getModal} from '@/utils/CommonModal'
const AddSalesTarget = () => {
  const location = useLocation()
  const isEdit = location?.state?.isEdit ?? false
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const salesTarget = useAppSelector(state => state?.salesTarget)
  const [isCancel, setIsCancel] = useState(false)
  const [formData, setFormData] = useState(salesTarget?.formData)
  const [isPrerequisiteTarget, setIsPrerequisiteTarget] = useState(false)
  const [isLayeredTarget, setIsLayeredTarget] = useState(false)
  const { handleDraft, handleNameValidation, isNameValid, setIsNameValid,handleValidationModalClose} = useSalesTargets(false)

  useEffect(() => {
    setIsPrerequisiteTarget(salesTarget.targetType === TARGET_TYPES.MonthlyTargetPrerequistite || salesTarget.targetType === TARGET_TYPES.ProductTargetPrerequisite)
    setIsLayeredTarget(salesTarget?.targetType === TARGET_TYPES.ProductTargetLayered || salesTarget?.targetType === TARGET_TYPES.MonthlyTargetLayered)
  }, [salesTarget.targetType])

  const handleNextAction = async () => {
    const isValidName = await validateName()
    if (!isValidName) return
    const data = { ...formData }
    data.displayStartDate = data?.activeDisplay ? data?.startDate : data?.displayStartDate
    data.displayEndDate = data?.activeDisplay ? data?.endDate : data?.displayEndDate
    dispatch(setSalesTargetFormData(data))
    navigate(ROUTES.SALES_TARGET_DISPLAY_CONTENT, {state: {isEdit: isEdit}})
  }

  const validateName = async () => {
    const targetName = formData?.targetName

    // Check if name is empty or whitespace only
    if (!targetName || String(targetName).trim() === '') {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Target Name is mandatory',
          type: 'error',
        })
      )
      return false
    }

      const id = salesTarget?.id ?? null
      const uuid = salesTarget?.uuid ?? null
      const params = {
        id,
        uuid,
        name: targetName,
      }
      if(params.id===1)
        delete params.id
      const resp = await handleNameValidation(params)
      const isValid = resp?.success === true
      setIsNameValid(isValid)
      return isValid
  }  

  const {
    targetType,
    targetNameValid,
    startDateValid,
    endDateValid,
    activeDisplay,
    displayStartDateValid,
    displayEndDateValid,
    targetDeliveryDateValid,
    awardPointsImmedFlag,
    pointsDeliveryDateValid,
    roundUpPointsFlag,
    roundOffValueValid,
    pointsDeliveryDate,
    isNumberOfLevelsSelected,
    isNumberOfMilestonesSelected,
    pointDisplay,
  } = formData

  const handleDraftAction = async () => {
    // When editing, merge the updated formData with the existing salesTarget data
    // When creating new, use formData as it only contains step 1 data
    try {
      const isValidName = await validateName()
      if (!isValidName) return
      const dataToSend = isEdit ? { ...salesTarget, ...formData } : formData;
      const response = await handleDraft(dataToSend, salesTarget, isPrerequisiteTarget);
      if (response.success) {
        navigate(ROUTES.SALES_TARGETS);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      return;
    }
  }

  const handleCancel = () => {
    setIsCancel(!isCancel)
  }
  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }
  const onClickPrimaryButton = () => {
    dispatch(resetSalesTarget())
    navigate(ROUTES.SALES_TARGETS)
  }

  const validateFormData = isValid => {
    return isValid === undefined || isValid === true
  }

  const isEmptyValue = (value) => {
    return !value || (typeof value === 'string' && value.trim() === '')
  }
 
  const commonFieldsValidation = () => {
 
    return !targetType ||
      validateFormData(targetNameValid) ||
      validateFormData(startDateValid) ||
      validateFormData(endDateValid) ||
      validateFormData(targetDeliveryDateValid) ||
      validateFormData(isNumberOfLevelsSelected) ||
      validateFormData(isNumberOfMilestonesSelected) ||
      (isLayeredTarget && isEmptyValue(pointDisplay))
  }

  const getFormError = () => {
    const hasCommonError = commonFieldsValidation()

    if (
      !activeDisplay &&
      (hasCommonError ||
        validateFormData(displayStartDateValid) ||
        validateFormData(displayEndDateValid) ||
        !formData?.displayStartDate ||
        !formData?.displayEndDate)
    ) {
      return true
    }

    if (!awardPointsImmedFlag && (hasCommonError || !pointsDeliveryDate || validateFormData(pointsDeliveryDateValid))) {
      return true
    }

    if (salesTarget?.storeList?.length === 0) {
      return true
    }
    if (roundUpPointsFlag) {
      const roundOffError = hasCommonError || validateFormData(roundOffValueValid)
      return roundOffError
    }

    return hasCommonError
  }

  const getCustomModal = (type) => {
    switch(type) {
      case "CANCEL_MODAL":
        return getModal({
          title: "Are you sure you want to cancel?",
          text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
          showPrimaryButton: true, 
          primaryButtonText: "Yes",
          onClickPrimaryButton: onClickPrimaryButton, 
          showSecondaryButton:   true,
          secondaryButtonText: "No",
          onClickSecondaryButton: onClickSecondaryButton, 
          showModal: isCancel, 
          onCloseModal: handleCancel, 
          hideCloseButton: false
        })
      case "NAME_VALIDATION_MODAL":
        return getModal({
          title: "Duplicate Name Detected",
          text: (
            <>
            It looks like there's already a Sales Target with this name.<br />
            To avoid confusion, please enter a unique name before continuing.
            </>
              ),
          showPrimaryButton: true, 
          primaryButtonText: "Edit Name",
          onClickPrimaryButton: handleValidationModalClose, 
          showSecondaryButton:   false,
          showModal: !isNameValid, 
          onCloseModal: handleValidationModalClose, 
          hideCloseButton: true
        })
      default:
        return null;
    }
  }
  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: '/',
              label: '',
            },
            {
              href: '/rewards/sales-targets',
              label: 'Loyalty Campaigns',
            },
            {
              href: '/rewards/sales-targets',
              label: 'Add Sales Target',
            },
          ],
        }}
        id={'ast_bradcrumb'}
      />
      <PageHeader title={'Add Sales Target'} />

      <ProgressIndicator
        currentStep={1}
        completed={false}
        orientation="horizontal"
        steps={4}
        titles={[{title: 'Setup'}, {title: 'Display Content'}, {title: 'Products'}, {title: 'Review'}]}
        className="mb-6"
      />
      <br />
      <TargetSetUp setFormData={setFormData} formData={formData}  salesTarget={salesTarget}/>
      {isCancel && getCustomModal("CANCEL_MODAL") }
      {!isNameValid && getCustomModal("NAME_VALIDATION_MODAL")}
      <SaveCancelFooter
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
      />
    </>
  )
}

export default AddSalesTarget
